/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */

import styles from './blocks.module.css' with { type: 'css' };
import { NoteMenu } from './note-menu';
import { toInt } from '../../audio';
import {TunePadCell} from "../cell";

/**
 * Timeline can show midi piano roll or waveform view of audio
 * 
 * <tunepad-block-menu>
 *     <tunepad-block code="playNote($0)">
 *       playNote <div class="param" data-menu="pitch">60</div>
 *     </tunepad-block>
 *     <tunepad-block code="for i in range($0):" color="pink">
 *       repeat <div class="param" data-type="int">8</div> times
 *     </tunepad-block>
 * <tunepad-block-menu>
 */
export class BlockMenu extends HTMLElement {

    static readonly ELEMENT = "tunepad-block-menu";

    static observedAttributes = [ ];

    private root : ShadowRoot;
    private menu : HTMLDivElement;
    private target? : HTMLDivElement;


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.menu = document.createElement('div');
        this.menu.classList.add('tunepad-block-menu');
        this.root.append(this.menu);

        // we have to put these styles in the top-level document as well because 
        // when the blocks are being dragged, they are direct children of the <body>
        document.adoptedStyleSheets.push(styles);
    }

    async connectedCallback() {
        await this.waitForWindowLoad();

        // move content into the shadow root
        this.menu.innerHTML = this.innerHTML;

        // set up block event handlers
        this.menu.querySelectorAll('tunepad-block').forEach(block => {
            block.addEventListener('pointerdown', (e) => {
                this.target = document.createElement('div');
                this.target.classList.add('tunepad-drag-block');
                const code = this.formatCode(block as HTMLElement);
                this.target.innerHTML = code.replace('\n', '<br>').replace('\\t', '&nbsp;&nbsp;&nbsp;&nbsp;');
                this.target.style.left = `${(e as PointerEvent).clientX - 25}px`;
                this.target.style.top = `${(e as PointerEvent).clientY - 15 + window.scrollY}px`;
                if (block.getAttribute('color')) {
                    this.target.style.backgroundColor = block.getAttribute('color')!;
                }
                document.body.appendChild(this.target);
                sessionStorage.setItem('tunepad-drag-block', code);
            });
        });

        document.addEventListener('pointermove', (e) => {
            if (this.target) {
                this.target.style.left = `${e.clientX - 25}px`;
                this.target.style.top = `${e.clientY - 15 + window.scrollY}px`;
            }
        });

        document.addEventListener('pointerup', (e) => {
            if (this.target) {
                const hits = document.elementsFromPoint(e.clientX, e.clientY);
                hits.forEach(hit => {
                   if(hit.nodeName.toLowerCase() == "tunepad-cell"){
                       (hit as TunePadCell).blockDropped(e.clientX, e.clientY, sessionStorage.getItem('tunepad-drag-block')!);
                   }
                });

                this.target.classList.add('fade');
                setTimeout(() => {
                    this.target?.remove();
                    this.target = undefined;
                    sessionStorage.removeItem('tunepad-drag-block');
                }, 300);
            }
        });

        // activate parameter menus
        this.root.querySelectorAll('.param').forEach(p => {
            p.addEventListener('pointerdown', async (e) => {
                e.stopImmediatePropagation();
                const menu = p.getAttribute('data-menu');
                if (menu) {
                    const value = p.innerHTML;
                    const selection = await NoteMenu.show(menu, value);
                    p.innerHTML = selection;
                }
            });
            if (p.getAttribute('data-type') === 'int') {
                const originalValue = toInt(p.innerHTML, 1);
                p.addEventListener('blur', e => {
                    p.innerHTML = `${toInt(p.innerHTML, originalValue)}`;
                });
                p.addEventListener('keypress', e => {
                    if ((e as KeyboardEvent).key === "Enter") (p as HTMLElement).blur();
                });
            }
        });
    }

    disconnectedCallback() { }

    attributeChangedCallback(name : string, oldValue : string, newValue : string) { }


    private formatCode(block : HTMLElement) : string {
        let template = block.getAttribute('code') || 'playNote(60)';
        let index = 0;
        block.querySelectorAll('.param').forEach(p => {
            template = template.replace('$' + index, p.innerHTML);
            index++;
        });
        return template.replaceAll('\\n', '\n');
    }

    
    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') { 
                resolve(); 
            } else {
                window.addEventListener('load', () => { resolve(); });
            }
        });
    }
}


export class Block extends HTMLElement {
    static readonly ELEMENT = "tunepad-block";

    static observedAttributes = [ 'color' ];

    constructor() { super(); }

    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (name === 'color') {
            this.style.backgroundColor = newValue;
        }
    }

    connectedCallback() { }

    disconnectedCallback() { }
}
